import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const sourceColorMap = {
    upload: 'default',
    download: 'info',
    mediafile: 'secondary',
    ocr_document: 'warning',
};

const UserFilesPage = ({
    listUrl,
    deleteUrl,
    managedFilesUsageBytes,
    managedFilesUsageLabel,
    managedFilesLimitBytes,
    managedFilesLimitLabel,
    labelsUrl,
}) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [usageBytes, setUsageBytes] = useState(managedFilesUsageBytes ?? 0);
    const [usageLabel, setUsageLabel] = useState(managedFilesUsageLabel ?? '0 B');
    const [previewFile, setPreviewFile] = useState(null);
    const [previewZoom, setPreviewZoom] = useState(1);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const csrfToken = useMemo(() => getCsrfToken(), []);

    const loadFiles = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(listUrl, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Unable to load user files.');
            }

            const payload = await response.json();
            setFiles(Array.isArray(payload?.data) ? payload.data : []);
            if (payload?.managed_files_usage_bytes !== undefined) {
                setUsageBytes(payload.managed_files_usage_bytes);
            }
            if (payload?.managed_files_usage_label) {
                setUsageLabel(payload.managed_files_usage_label);
            }
            setError('');
        } catch (requestError) {
            setFiles([]);
            setError(requestError.message ?? 'Unable to load user files.');
        } finally {
            setLoading(false);
        }
    }, [listUrl]);

    useEffect(() => {
        loadFiles();
    }, [loadFiles]);

    const filteredFiles = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return files.filter((file) => {
            if (sourceFilter !== 'all' && file.source !== sourceFilter) {
                return false;
            }

            if (normalizedSearch === '') {
                return true;
            }

            const haystack = [
                file.name,
                file.path_label,
                file.source_label,
                file.size_label,
                file.meta?.status_label,
                file.meta?.type,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return haystack.includes(normalizedSearch);
        });
    }, [files, searchTerm, sourceFilter]);

    const paginatedFiles = useMemo(() => {
        const startIndex = page * rowsPerPage;

        return filteredFiles.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredFiles, page, rowsPerPage]);

    useEffect(() => {
        setPage(0);
    }, [searchTerm, sourceFilter]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredFiles.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [filteredFiles.length, page, rowsPerPage]);

    const handleDelete = useCallback(async (file) => {
        const confirmed = window.confirm(
            `Delete ${file.name}?\n\nThis action cannot be undone and there is no restore option. Some functionality may stop working if this file is still referenced elsewhere.`
        );
        if (!confirmed) {
            return;
        }

        setBusyId(file.id);
        try {
            let response;
            if (file.delete_mode === 'direct' && file.delete_url) {
                response = await fetch(file.delete_url, {
                    method: 'DELETE',
                    headers: {
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    credentials: 'same-origin',
                });
            } else {
                response = await fetch(deleteUrl, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify(file.delete_payload || {}),
                });
            }

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload?.message ?? 'Unable to delete the selected file.');
            }

            await loadFiles();
        } catch (deleteError) {
            setError(deleteError.message ?? 'Unable to delete the selected file.');
        } finally {
            setBusyId('');
        }
    }, [csrfToken, deleteUrl, loadFiles]);

    const usageHint = managedFilesLimitBytes === null
        ? `${usageLabel} / ${managedFilesLimitLabel}`
        : `${usageLabel} / ${managedFilesLimitLabel}`;

    const handleOpenPreview = useCallback((file) => {
        setPreviewFile(file);
        setPreviewZoom(1);
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewFile(null);
        setPreviewZoom(1);
    }, []);

    const handleChangePage = useCallback((_, nextPage) => {
        setPage(nextPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    return (
        <Box>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h6">User files</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Review every quota-counted upload, download, media file, and OCR document from this owner scope.
                    </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Chip color="info" label={`User files: ${usageHint}`} />
                    <Button variant="outlined" href={labelsUrl}>Labels page</Button>
                </Stack>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
                Labels are intentionally excluded from this page because they already have a dedicated management surface.
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }} sx={{ mb: 2 }}>
                <TextField
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search files"
                    size="small"
                    sx={{ width: { xs: '100%', lg: 360 } }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="user-files-source-filter-label">Source</InputLabel>
                        <Select
                            labelId="user-files-source-filter-label"
                            value={sourceFilter}
                            label="Source"
                            onChange={(event) => setSourceFilter(event.target.value)}
                        >
                            <MenuItem value="all">All sources</MenuItem>
                            <MenuItem value="upload">Uploads</MenuItem>
                            <MenuItem value="download">Downloads</MenuItem>
                            <MenuItem value="mediafile">Media files</MenuItem>
                            <MenuItem value="ocr_document">OCR documents</MenuItem>
                        </Select>
                    </FormControl>
                    {!loading && files.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Showing {filteredFiles.length} of {files.length} files
                        </Typography>
                    )}
                </Stack>
            </Stack>

            {loading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2">Loading user files...</Typography>
                </Stack>
            ) : files.length === 0 ? (
                <Alert severity="info">No quota-counted files were found for this user.</Alert>
            ) : filteredFiles.length === 0 ? (
                <Alert severity="info">No files match the current filters.</Alert>
            ) : (
                <>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Source</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>Updated</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedFiles.map((file) => (
                                <TableRow key={file.id} hover>
                                    <TableCell sx={{ width: 150 }}>
                                        <Stack spacing={0.5}>
                                            <Chip
                                                label={file.source_label}
                                                color={sourceColorMap[file.source] || 'default'}
                                                size="small"
                                                sx={{ width: 'fit-content' }}
                                            />
                                            {file.meta?.status_label && (
                                                <Typography variant="caption" color="text.secondary">{file.meta.status_label}</Typography>
                                            )}
                                            {file.meta?.type && (
                                                <Typography variant="caption" color="text.secondary">{file.meta.type}</Typography>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{file.path_label}</Typography>
                                    </TableCell>
                                    <TableCell>{file.size_label}</TableCell>
                                    <TableCell>{file.updated_at_label}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.25} justifyContent="flex-end" flexWrap="nowrap" alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
                                            {file.preview_url && file.preview_kind && (
                                                <Tooltip title="View file">
                                                    <IconButton size="small" onClick={() => handleOpenPreview(file)} aria-label="View file">
                                                        <VisibilityOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {file.details_url && file.source !== 'mediafile' && (
                                                <Tooltip title="Open details">
                                                    <IconButton size="small" component="a" href={file.details_url} aria-label="Open details">
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {file.download_url && (
                                                <Tooltip title="Download file">
                                                    <IconButton size="small" component="a" href={file.download_url} target="_blank" rel="noreferrer" aria-label="Download file">
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title={busyId === file.id ? 'Deleting...' : 'Delete file'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        disabled={busyId === file.id}
                                                        onClick={() => handleDelete(file)}
                                                        aria-label={busyId === file.id ? 'Deleting file' : 'Delete file'}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={filteredFiles.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </>
            )}

            <Dialog open={Boolean(previewFile)} onClose={handleClosePreview} fullScreen>
                <DialogTitle>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Box>
                            <Typography variant="subtitle1">File preview</Typography>
                            <Typography variant="caption" color="text.secondary">{previewFile?.name || ''}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {previewFile?.preview_kind === 'image' && (
                                <>
                                    <IconButton onClick={() => setPreviewZoom((currentZoom) => Math.max(0.5, Number((currentZoom - 0.25).toFixed(2))))}>
                                        <ZoomOutIcon />
                                    </IconButton>
                                    <IconButton onClick={() => setPreviewZoom(1)}>
                                        <RestartAltIcon />
                                    </IconButton>
                                    <IconButton onClick={() => setPreviewZoom((currentZoom) => Math.min(4, Number((currentZoom + 0.25).toFixed(2))))}>
                                        <ZoomInIcon />
                                    </IconButton>
                                </>
                            )}
                            {previewFile?.download_url && (
                                <Button variant="outlined" color="inherit" component="a" href={previewFile.download_url} target="_blank" rel="noreferrer" startIcon={<OpenInFullIcon />}>Download</Button>
                            )}
                            <Button variant="outlined" color="inherit" onClick={handleClosePreview} startIcon={<CloseIcon />}>Close</Button>
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, backgroundColor: '#111' }}>
                    {previewFile?.preview_kind === 'image' ? (
                        <Box sx={{ height: 'calc(100vh - 96px)', overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', p: 3 }}>
                            <Box
                                component="img"
                                src={previewFile.preview_url}
                                alt={previewFile.name}
                                sx={{
                                    maxWidth: '100%',
                                    transform: `scale(${previewZoom})`,
                                    transformOrigin: 'top center',
                                    transition: 'transform 120ms ease-out',
                                }}
                            />
                        </Box>
                    ) : previewFile?.preview_kind === 'video' ? (
                        <Box sx={{ height: 'calc(100vh - 96px)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                            <Box component="video" controls src={previewFile.preview_url} sx={{ maxWidth: '100%', maxHeight: '100%' }} />
                        </Box>
                    ) : previewFile?.preview_kind === 'audio' ? (
                        <Box sx={{ height: 'calc(100vh - 96px)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                            <Box component="audio" controls src={previewFile.preview_url} sx={{ width: '100%', maxWidth: 720 }} />
                        </Box>
                    ) : (
                        <Box component="iframe" src={previewFile?.preview_url || ''} title="User file preview" sx={{ width: '100%', height: 'calc(100vh - 96px)', border: 0, backgroundColor: '#f5f5f5' }} />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

UserFilesPage.propTypes = {
    listUrl: PropTypes.string.isRequired,
    deleteUrl: PropTypes.string.isRequired,
    managedFilesUsageBytes: PropTypes.number,
    managedFilesUsageLabel: PropTypes.string,
    managedFilesLimitBytes: PropTypes.number,
    managedFilesLimitLabel: PropTypes.string,
    labelsUrl: PropTypes.string.isRequired,
};

UserFilesPage.defaultProps = {
    managedFilesUsageBytes: 0,
    managedFilesUsageLabel: '0 B',
    managedFilesLimitBytes: null,
    managedFilesLimitLabel: 'Unlimited',
};

export default UserFilesPage;